import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ColorPalette from '../../../../colorpalette/ColorPalette';

const SECONDS = 5;

/**
 * Sobreposição no fim do vídeo: contagem regressiva e "Próximo vídeo: título".
 * Ao chegar a zero chama onNext; "Cancelar" fecha sem avançar.
 * @param {String} nextTitle - título da próxima lição
 * @param {Function} onNext
 * @param {Function} onCancel
 */
const NextLessonCountdown = ({ nextTitle, onNext, onCancel }) => {
  const [remaining, setRemaining] = useState(SECONDS);
  // Avança uma única vez: depois do avanço o pai troca onNext e re-renderiza com remaining 0
  const firedRef = useRef(false);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  const advance = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onNextRef.current();
  };

  useEffect(() => {
    if (remaining <= 0) {
      advance();
      return undefined;
    }
    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: 3,
        textAlign: 'center',
        backgroundColor: 'rgba(26, 26, 46, 0.72)',
        backdropFilter: 'blur(2px)'
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={(remaining / SECONDS) * 100}
          size={72}
          thickness={3}
          sx={{ color: '#FFFFFF', '& .MuiCircularProgress-circle': { transition: 'stroke-dashoffset 1s linear' } }}
        />
        <Typography
          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: '1.6rem', fontWeight: 700 }}
        >
          {remaining}
        </Typography>
      </Box>
      <Typography sx={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Próximo vídeo
      </Typography>
      <Typography sx={{ color: '#FFFFFF', fontSize: { xs: '1.05rem', md: '1.3rem' }, fontWeight: 700, lineHeight: 1.3, maxWidth: 520 }}>
        {nextTitle}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', color: '#FFFFFF' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={advance}
          startIcon={<PlayArrowIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', px: 2.5, backgroundColor: '#FFFFFF', color: ColorPalette.blueMain, '&:hover': { backgroundColor: '#F1F4FF' } }}
        >
          Assistir agora
        </Button>
      </Box>
    </Box>
  );
};

export default NextLessonCountdown;
